import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import DeleteProjectModal from "../../components/DeleteProjectModal";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";
import { graphqlEndpoint } from "../../config";

import { READ_PROJECTS_FILTER } from "../../graph/projects/queries";
import {
  DELETE_PROJECT,
  INSTANTIATE_SOP,
} from "../../graph/projects/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const ManageSOPs = () => {
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();

  const [projects, setProjects] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const [instModalOpen, setInstModalOpen] = useState(false);
  const [instSopId, setInstSopId] = useState(null);
  const [instSopTitle, setInstSopTitle] = useState("");
  const [instanceName, setInstanceName] = useState("");

  const fetchProjects = useCallback(async () => {
    if (!tenantId) {
      setProjects([]);
      return;
    }

    try {
      const data = await client.request(READ_PROJECTS_FILTER, {
        filter: {
          tenantId,
          isSOPTemplate: true,
        },
      });

      const formattedProjects = (data.readProjectsFilter || []).map((project) => ({
        ...project,
        id: project.id,
        labels: project.labels?.join(", ") || "",
        taskCount:
          project.tasks?.filter((task) => task.isLatest !== false).length || 0,
        sourceProjectTitle: project.sourceProject?.title || "—",
      }));

      setProjects(formattedProjects);
    } catch (err) {
      console.error("Failed to fetch SOP projects:", err);
      setProjects([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load SOPs",
      });
    }
  }, [tenantId, setSnackbar]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleOpenDeleteModal = (project) => {
    setSelectedProject(project);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedProject(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedProject?.id) return;

    try {
      await client.request(DELETE_PROJECT, { id: selectedProject.id });
      await fetchProjects();
      handleCloseDeleteModal();

      setSnackbar({
        open: true,
        severity: "success",
        message: "SOP deleted successfully",
      });
    } catch (err) {
      console.error("Failed to delete project:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete SOP",
      });
    }
  };

  const handleEdit = (id) => {
    navigate(`/sops/edit/${id}`, {
      state: {
        redirectPath: "/manageSOPs",
      },
    });
  };

  const handleView = (id) => {
    navigate(`/sops/read/${id}`);
  };

  const openInstantiate = (row) => {
    setInstSopId(row.id);
    setInstSopTitle(row.title);
    setInstanceName("");
    setInstModalOpen(true);
  };

  const closeInstantiate = () => {
    setInstModalOpen(false);
    setInstSopId(null);
    setInstSopTitle("");
    setInstanceName("");
  };

  const submitInstantiate = async () => {
    if (!instSopId || !instanceName.trim()) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Please enter a name",
      });
      return;
    }

    try {
      const data = await client.request(INSTANTIATE_SOP, {
        input: {
          id: instSopId,
          instanceName: instanceName.trim(),
          copyStatus: false,
        },
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Project instance created",
      });

      closeInstantiate();

      const newProjectId = data?.instantiateSOP?.id;
      if (newProjectId) {
        navigate(`/projects/edit/${newProjectId}`, {
          state: { redirectPath: "/manageProjects" },
        });
      } else {
        navigate("/manageProjects");
      }
    } catch (err) {
      console.error("Instantiate failed:", err);
      const message =
        err?.response?.errors?.[0]?.message || "Failed to instantiate SOP";
      setSnackbar({
        open: true,
        severity: "error",
        message,
      });
    }
  };

  const columns = [
    { field: "title", headerName: "Title", flex: 0.9 },
    { field: "description", headerName: "Description", flex: 1.3 },
    { field: "labels", headerName: "Labels", flex: 1 },
    { field: "taskCount", headerName: "Tasks", flex: 0.4 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onView={() => handleView(row.id)}
          onInstantiate={() => openInstantiate(row)}
          onEdit={() => handleEdit(row.id)}
          onDelete={() => handleOpenDeleteModal(row)}
          hideCreate
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="MANAGE SOPS"
          subtitle={
            tenantName
              ? `SOPs define how the work is performed for ${tenantName}`
              : "SOPs define how the work is performed"
          }
        />

        <ActionButton
          tone="gold"
          onClick={() =>
            navigate("/createProject", {
              state: {
                sop: true,
                name: "SOP",
                redirectPath: "/manageSOPs",
              },
            })
          }
          disabled={!tenantId}
        >
          Create SOP
        </ActionButton>
      </Box>

      {!tenantId ? (
        <Box mt={4}>
          <Typography variant="h6">
            Select a tenant in the top bar to view SOPs.
          </Typography>
        </Box>
      ) : (
        <ThemedDataGrid rows={projects} columns={columns} />
      )}

      <DeleteProjectModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        project={selectedProject}
      />

      <Dialog
        open={instModalOpen}
        onClose={closeInstantiate}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Instantiate “{instSopTitle}”</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Instance name"
            placeholder="e.g. Build bike for client X"
            value={instanceName}
            onChange={(e) => setInstanceName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitInstantiate()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeInstantiate}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={submitInstantiate}
          >
            Create Project
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageSOPs;